import MojitoCore from 'mojito/core';
import { STORE_KEY, INITIAL_STATE } from './slice.js';
import ServicesTypes from 'services/common/types.js';

const reduxInstance = MojitoCore.Services.redux;
const { UNKNOWN } = ServicesTypes.CONTENT_STATE;

/**
 * Event groups store selectors.
 *
 * @class EventGroupsSelectors
 * @name selectors
 * @memberof Mojito.Services.EventGroups
 */

/**
 * Selects event groups state.
 *
 * @function selectState
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.EventGroups.EventGroupsState} The state of the event groups store.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectState = state => {
    // Should be no need to fallback to global state once fully migrated to Redux.
    const appState = state || reduxInstance.store?.getState();
    return { ...INITIAL_STATE, ...appState?.[STORE_KEY] };
};

/**
 * Select a single event group.
 *
 * @function selectEventGroup
 *
 * @param {string} eventGroupId - Id of event group.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array} Array of groups in event group.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroup = (eventGroupId, state) => selectEventGroups(state)[eventGroupId];

/**
 * Select event groups.
 *
 * @function selectEventGroups
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {object} Event groups map. Key is event group id and value is array of groups.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroups = state => selectState(state).eventGroups;

/**
 * Select event groups by ids.
 *
 * @callback EventGroupsByIdsSelector
 *
 * @param {Array} groupIds - Group IDs to select.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Map<string, Array<object>>} Event groups map. Key is event group id and value is array of groups.
 * @memberof Mojito.Services.EventGroups.selectors
 */

/**
 * Select event group names.
 * Name is typically present on content coming from CMS and not part of event group object and therefore stored seperately from event groups.
 *
 * @function selectEventGroupNames
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {object} Event group names map. Key is event group id and value is event group name.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroupNames = state => selectState(state).eventGroupNames;

/**
 * Select event group name.
 * Name is typically present on content coming from CMS and not part of event group object and therefore stored seperately from event groups.
 *
 * @function selectEventGroupName
 *
 * @param {string} eventGroupId - Id of event group.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {string} Event group name.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroupName = (eventGroupId, state) =>
    selectEventGroupNames(state)[eventGroupId];

/**
 * Select event group market options.
 * This info, typically, defines the presence of market selectors or game line switchers for a coupon.
 *
 * @function selectEventGroupMarketOptions
 *
 * @param {string} eventGroupId - Id of event group.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<Mojito.Services.EventGroups.types.MarketOption>} Array of market options for specified event group id.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroupMarketOptions = (eventGroupId, state) =>
    selectState(state).marketOptions[eventGroupId];

/**
 * Select event group title.
 *
 * @function selectEventGroupTitle
 *
 * @param {string} eventGroupId - Id of event group.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {string} Title for specified event group id.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroupTitle = (eventGroupId, state) =>
    selectState(state).title[eventGroupId];

/**
 * Select event groups market options by sportId.
 * This info, typically, defines the presence of market selectors or game line switchers for a coupon.
 *
 * @function selectEventGroupMarketOptionsBySportId
 *
 * @param {string} eventGroupId - Id of event group.
 * @param {string} sportId - Id of sport.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<Mojito.Services.EventGroups.types.MarketOption>} Array of market options for specified sport and event group id.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroupMarketOptionsBySportId = (eventGroupId, sportId, state) => {
    return selectState(state).eventGroups[eventGroupId]?.find(group => group.sportId === sportId)
        .marketOptions;
};

/**
 * Selects event group item lifetime state in a store.
 *
 * @function selectEventGroupState
 *
 * @param {string} eventGroupId - Events group id.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.Common.types.CONTENT_STATE} Lifetime state of specific event group.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectEventGroupState = (eventGroupId, state) => {
    const { eventGroupsState } = selectState(state);
    return eventGroupsState[eventGroupId] || UNKNOWN;
};

/**
 * Select whether the event group contains a single league group.
 *
 * @function selectSingleLeagueGroups
 *
 * @param {string} eventGroupId - Id of event group.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {boolean} True when the coupon resolves to a single league group, false otherwise.
 * @memberof Mojito.Services.EventGroups.selectors
 */
export const selectSingleLeagueGroups = (eventGroupId, state) =>
    selectState(state).singleLeagueGroups[eventGroupId];
